import * as config from 'api/config'
import * as loanStatus from 'utils/LoanStatus'

import { AxiosRequestConfig, ResponseType } from 'axios'

import { BorrowerApiContract } from './BorrowerApiContract'
import { Loan, UploadedFile } from './BorrowerApiSchemas'
import { ErrorResponse } from './ErrorResponse'
import { authSession } from './AuthServiceConfig'

export abstract class BorrowerApi extends BorrowerApiContract {
  abstract fetchMultiple<T>(urls: string[]): Promise<T[]>

  getLoans(): Promise<Loan[]> {
    return this.getLoanSummaryList()
      .then((res) => {
        const urls = res.map((l) => `/loans/${l.reference}`)
        return this.fetchMultiple<Loan>(urls)
      })
      .then((loans) => {
        // Display order: Active loan most recent, Active loan oldest, Written-off
        const priority = loanStatus.PriorityLoanLists
        loans = loans.sort((a, b) => {
          return priority.indexOf(a.plan.status) - priority.indexOf(b.plan.status)
        })

        return loans.sort((a, b) => {
          return a.plan.endDate > b.plan.endDate ? -1 : 1
        })
      })
  }
}

class AxiosBorrowerApi extends BorrowerApi {
  private accessToken: () => Promise<string>

  constructor(tokenManager: () => Promise<string>) {
    super()
    this.accessToken = tokenManager
  }

  fetchObject<T>(url: string, responseType: ResponseType, searchParams: URLSearchParams | null = null): Promise<T> {
    return this.requestConfig(undefined, responseType, searchParams).then((requestConfig) =>
      config.httpProxy
        .get(url, requestConfig)
        .then((res) => {
          return res.data as T
        })
        .catch((error) => {
          if (error.response !== undefined) {
            return Promise.reject(new ErrorResponse(error.response.status, error.response.data))
          } else {
            return Promise.reject(error)
          }
        }),
    )
  }

  postObject<T>(url: string, body: unknown, contentType: string): Promise<T> {
    return this.requestConfig(contentType, 'json')
      .then((requestConfig) => {
        // Defensive: never send a boundary-less 'multipart/form-data' header (TED-4462). Uploads go
        // through uploadFile() below, but the generated contract still routes multipart via
        // postObject, so leave the browser to set Content-Type (with its boundary) for FormData.
        if (body instanceof FormData && requestConfig.headers) {
          delete (requestConfig.headers as Record<string, unknown>)['Content-Type']
        }
        return config.httpProxy.post(url, body, requestConfig).then((res) => {
          return res.data as T
        })
      })
      .catch((error) => {
        return Promise.reject(new ErrorResponse(error.response.status, error.response.data))
      })
  }

  // Upload via native fetch rather than axios/XHR. On Safari the XHR path sends an empty multipart
  // body (the request goes out with Content-Length: 0 despite a correct boundary), so document
  // uploads fail only in Safari (TED-4462); fetch lets the browser build the body correctly. Mirror
  // the axios instance's 45s timeout and normalise transport failures to an ErrorResponse so the
  // callers' `error.data.message` handling still works (fetch otherwise rejects with a TypeError).
  async uploadFile(body: FormData): Promise<UploadedFile> {
    const token = await this.accessToken()
    const genericError = 'We could not upload your file. Please try again.'

    // Restore the 45s timeout the axios instance had (config.httpProxy). AbortController + setTimeout
    // is used rather than AbortSignal.timeout for broad browser support (this fix exists to work
    // around a browser quirk, so it must not depend on a newer API).
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 45_000)

    let response: Response
    try {
      response = await fetch(`${config.apiUrl}/api/v1/commands/upload-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
        signal: controller.signal,
      })
    } catch {
      // Network/CORS failure or the 45s timeout (AbortError) — neither carries an ErrorResponse.
      throw new ErrorResponse(0, { message: genericError })
    } finally {
      clearTimeout(timeout)
    }

    const data = (await response.json().catch(() => ({}))) as { message?: string }
    if (!response.ok) {
      throw new ErrorResponse(response.status, { message: data.message ?? genericError })
    }
    return data as UploadedFile
  }

  fetchMultiple<T>(urls: string[]): Promise<T[]> {
    return this.requestConfig(undefined, 'json').then((requestConfig) =>
      Promise.all(
        urls.map((url) => {
          return config.httpProxy
            .get(url, requestConfig)
            .then((res) => {
              return res.data as T
            })
            .catch((error) => {
              return Promise.reject(new ErrorResponse(error.response.status, error.response.data))
            })
        }),
      ),
    )
  }

  private requestConfig(
    contentType = 'application/json',
    responseType: ResponseType,
    searchParams: URLSearchParams | null = null,
  ): Promise<AxiosRequestConfig> {
    return this.accessToken().then((token) => {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': contentType,
        },
        responseType: responseType,
        params: config.filterEmptyParams(searchParams),
      }
    })
  }
}

/**
 * An API client that presents the customer's auth-service access token (ADR-017 / TED-4200).
 *
 * Every call resolves its token through [AuthSession.getOrRefreshToken], which renews proactively about a
 * minute before expiry via `POST /api/v1/auth/refresh`. That replaces Okta's silent-iframe renewal: there is
 * no hidden iframe and no third-party cookie involved, just the `fluro_refresh` HttpOnly cookie.
 *
 * Unlike the Okta version this no longer throws when there is no session. A rejected token promise fails the
 * individual call, and `RequiredAuth` is what routes an unauthenticated customer to sign-in — throwing here
 * as well meant a displaced session surfaced as a render-time crash rather than a redirect.
 */
export function borrowerApiClient(
  tokenProvider: () => Promise<string> = () => authSession.getOrRefreshToken(),
): BorrowerApi {
  return new AxiosBorrowerApi(tokenProvider)
}
